/**
 * Cart Upsell — fetches complementary product recommendations and renders
 * them inside .cart-upsell__inner. After quick-add, dispatches the theme's
 * native 'cart:update' event so cart-items-component refreshes via morphSection.
 */
(function () {
  'use strict';

  const CART_UPDATE_EVENT = 'cart:update';
  const UPSELL_SELECTOR = '.cart-upsell';

  // Simple in-memory cache: productId → products[]
  const cache = new Map();

  // ── Helpers ──────────────────────────────────────────────────────────────

  function money(cents) {
    return '₪' + (cents / 100).toFixed(0);
  }

  function escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function resizeImage(src, size) {
    if (!src) return '';
    // Insert _WxH before the extension, keep query string
    return src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i, '_' + size + '$1$3');
  }

  function getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  // ── Fetch recommendations ─────────────────────────────────────────────────

  async function fetchRecs(productId, intent) {
    const url =
      '/recommendations/products.json?product_id=' +
      productId +
      '&limit=4&intent=' +
      intent;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  async function getRecs(productId) {
    if (cache.has(productId)) return cache.get(productId);
    // Try complementary first, fall back to related
    let products = await fetchRecs(productId, 'complementary');
    if (!products.length) products = await fetchRecs(productId, 'related');
    const sliced = products.slice(0, 3);
    cache.set(productId, sliced);
    return sliced;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  function cardHtml(product) {
    const variant = product.variants && product.variants[0];
    if (!variant) return '';

    const imgSrc =
      product.featured_image && typeof product.featured_image === 'object'
        ? product.featured_image.src
        : typeof product.featured_image === 'string'
        ? product.featured_image
        : '';

    const imgTag = imgSrc
      ? '<img src="' +
        escape(resizeImage(imgSrc, '80x80')) +
        '" alt="' +
        escape(product.title) +
        '" class="cart-upsell__img" width="80" height="80" loading="lazy">'
      : '<div class="cart-upsell__img-placeholder"></div>';

    const priceHtml =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? '<s class="cart-upsell__compare">' +
          money(variant.compare_at_price) +
          '</s> ' +
          '<span>' +
          money(variant.price) +
          '</span>'
        : money(variant.price);

    return (
      '<li class="cart-upsell__item" dir="rtl">' +
      '<a href="' +
      escape(product.url) +
      '" class="cart-upsell__img-wrap" tabindex="-1" aria-hidden="true">' +
      imgTag +
      '</a>' +
      '<div class="cart-upsell__info">' +
      '<p class="cart-upsell__name">' +
      escape(product.title) +
      '</p>' +
      '<p class="cart-upsell__price">' +
      priceHtml +
      '</p>' +
      '</div>' +
      (product.variants.length > 1
        ? '<a href="' +
          escape(product.url) +
          '" class="cart-upsell__add cart-upsell__add--select">' +
          'בחר מידה' +
          '</a>'
        : '<button class="cart-upsell__add" data-variant-id="' +
          variant.id +
          '" aria-label="' +
          escape('הוסף ' + product.title + ' לעגלה') +
          '">הוסף</button>') +
      '</li>'
    );
  }

  function renderRecs(inner, products) {
    inner.innerHTML =
      '<h3 class="cart-upsell__title">מוצרים משלימים לסט</h3>' +
      '<ul class="cart-upsell__list" role="list">' +
      products.map(cardHtml).join('') +
      '</ul>';

    inner.querySelectorAll('.cart-upsell__add').forEach(function (btn) {
      btn.addEventListener('click', handleAdd);
    });
  }

  // ── Add to cart ───────────────────────────────────────────────────────────

  async function handleAdd(e) {
    const btn = e.currentTarget;
    const variantId = Number(btn.dataset.variantId);
    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = '...';

    const sections = getSectionIds();

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.status) {
        btn.textContent = 'הוסף';
        btn.disabled = false;
        return;
      }

      btn.textContent = '✓';
      btn.classList.add('cart-upsell__add--done');

      // Dispatch 'cart:update' — cart-items-component morphs section if
      // data.sections[sectionId] is present, otherwise re-fetches.
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'cart-upsell',
            data: {
              source: 'cart-upsell',
              itemCount: data.item_count,
              sections: data.sections || {},
            },
          },
        })
      );
    } catch (_err) {
      btn.textContent = 'הוסף';
      btn.disabled = false;
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  async function initUpsell() {
    const el = document.querySelector(UPSELL_SELECTOR);
    if (!el) return;

    const productId = el.dataset.productId;
    if (!productId) {
      el.hidden = true;
      return;
    }

    const inner = el.querySelector('.cart-upsell__inner');
    if (!inner) return;

    // After morphSection the inner is reset to empty; always re-render from cache
    try {
      const products = await getRecs(productId);
      if (!products.length) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      renderRecs(inner, products);
    } catch (_err) {
      el.hidden = true;
    }
  }

  // Run on load and after every cart update (morph clears inner content)
  function run() {
    initUpsell();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener(CART_UPDATE_EVENT, function () {
    // Small delay to allow morphSection to finish patching the DOM
    setTimeout(run, 180);
  });
})();
